import React, { useEffect, useMemo, useRef, useState } from "react";
import CodeMirror, { basicSetup } from '@uiw/react-codemirror';
import { javascript, javascriptLanguage } from '@codemirror/lang-javascript';
import { materialDark, materialLight } from '@uiw/codemirror-theme-material';
import { useSelector } from "react-redux";
import { autocompletion } from '@codemirror/autocomplete';
import * as formulajs from '@formulajs/formulajs';
import { functions } from "./functions";
import { Tabs, Collapse, List, Typography, Table, Button, Modal, Input, Radio, Form } from 'antd';
import _ from "lodash";
import { validateFormula } from "../../../../../hooks/dataOfForm";
import { allFormsOfSavedObjectsWithFields, getPreviewValueFormula } from "../../../../../auth/actions/objActions";
import { CopyOutlined } from "@ant-design/icons";
import moment from "moment";

export const Formula = ({
    fields = [],
    code = "",
    form,
    onChange,
    formName,
    id,
    field,
    getFieldType,
    completeFields = {},
    isValidationForm = false,
}) => {
    const { isDarkMode } = useSelector((state) => state.theme);
    const [forms, setForms] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await allFormsOfSavedObjectsWithFields();
                setForms(data.message);
            } catch (error) {
                console.error("Error fetching data: ", error);
                // Set state in error case as needed
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        setValue(code);
        setShowPreview(false);
    }, [code])

    const editorRef = useRef();

    const { Panel } = Collapse;

    const formulaFunctions = Object.keys(formulajs);

    const [formFields, setFormFields] = useState(fields);
    const [value, setValue] = useState(code);
    const [loading, setLoading] = useState(false);


    // pre-visualizacao valor da formula
    const [previewData, setPreviewData] = useState([]);
    const [previewColumns, setPreviewColumns] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    async function previewFormulaHandler() {
        setLoading(true);
        try {
            const idFieldFormula = completeFields.find((item) => item.field === field)?._id ?? null;
            if (!idFieldFormula) {
                Modal.warn({
                    title: "Erro ao buscar ID",
                    content: "Não foi possível encontrar o ID do campo fórmula para pré visualização dos dados."
                });
                return;
            }

            const response = await getPreviewValueFormula({
                formName: formName,
                valueFormula: value,
                idFieldFormula: idFieldFormula,
                typePreview: "FIRST_RECORDS",
                fakeFields: {}
            });

            if (!response?.success) {
                Modal.error({
                    title: "Erro ao buscar informações",
                    content: response?.message || "Erro ao obter os dados de pré-visualização."
                });
                return;
            }

            const rawData = response.message;

            if (!Array.isArray(rawData) || rawData.length === 0) {
                Modal.info({
                    title: "Sem dados para pré-visualizar",
                    content: "Não há registros disponíveis para esta fórmula no momento.",
                });
                return;
            }

            const data = rawData.map((row, index) => {
                const formattedRow = { key: index.toString() };

                Object.entries(row).forEach(([fieldName, obj]) => {
                    const value = obj?.value;

                    if (typeof value === 'string' && moment(value, moment.ISO_8601, true).isValid()) {
                        formattedRow[fieldName] = moment(value);
                    } else {
                        formattedRow[fieldName] = value;
                    }

                });
                return formattedRow;
            });

            const firstRow = data[0];
            const columns = Object.keys(firstRow)
                .filter((key) => key !== "key")
                .map((key) => ({
                    title: key,
                    dataIndex: key,
                    key,
                    ellipsis: true,
                    render: (value) =>
                        moment.isMoment(value) ? value.format("DD/MM/YYYY") : value ?? "-",
                }));

            setPreviewColumns(columns);
            setPreviewData(data);
            setShowPreview(true);

        } catch (err) {
            Modal.error({
                title: "Erro inesperado.",
                content: err.message || "Ocorreu um problema ao tentar gerar a pré-visualização."
            });
        } finally {
            setLoading(false)
        }
    }

    const completions = (context) => {
        let word = context.matchBefore(/\w*/);
        if (!word) return null;
        return {
            from: word.from,
            options: [
                ...formulaFunctions.map((func) => ({
                    label: func,
                    type: 'keyword',
                })),
                ...formFields.map(({ field }) => ({
                    label: `[${field}]`,
                    type: 'variable',
                })),
                ...forms.map(({ name }) => ({
                    label: `"${name.replace("obj_", "")}"`,
                    type: 'variable',
                })),
                ..._.flatten(forms.map(({ fields }) => fields.map(({ field }) => ({
                    label: `"${field}"`,
                    type: 'variable',
                })))),
            ],
        };
    };

    const functionsCollapseRender = () => {
        // 
        const grouped = _.groupBy(functions, "category");

        //
        const groupedFunctions = _.mapValues(grouped, (items) => _.sortBy(items, "name"));

        //
        const defaultActiveKey = Object.entries(groupedFunctions)[0][0];
        return (
            <Collapse defaultActiveKey={defaultActiveKey}>
                {
                    _.map(
                        groupedFunctions,
                        (value, categoryName) => {
                            const columns = [
                                {
                                    title: 'Nome',
                                    dataIndex: 'name',
                                    key: 'name',
                                },
                                {
                                    title: 'Descrição',
                                    dataIndex: 'description',
                                    key: 'description',
                                },
                                {
                                    title: 'Parâmetros',
                                    dataIndex: 'params',
                                    key: 'params',
                                    render: params => {
                                        return (params && _.isArray(params) ? params.map(param => <p><Typography.Text
                                            code>{param.name}</Typography.Text> - {param.description}</p>) : '-');
                                    }
                                },
                                {
                                    title: 'Exemplo',
                                    dataIndex: 'example',
                                    key: 'example',
                                },
                                {
                                    title: 'Resultado',
                                    dataIndex: 'result',
                                    key: 'result',
                                },
                            ];

                            return (
                                <Panel header={categoryName} key={categoryName} ghost>
                                    <Table
                                        dataSource={value}
                                        columns={columns}
                                        size='small' scroll={{ y: 200, x: 200 }}
                                        pagination={false}
                                        onRow={(record, rowIndex) => {
                                            return {
                                                onDoubleClick: event => {
                                                    const { name, params } = record;  // obtém o nome da função e os parâmetros
                                                    if (editorRef.current) {
                                                        const editor = editorRef.current.view;
                                                        const insertPos = editor.state.selection.main.head;
                                                        const functionNameWithParentheses = `${name}()`;
                                                        const transaction = editor.state.update({
                                                            changes: {
                                                                from: insertPos,
                                                                insert: functionNameWithParentheses
                                                            },
                                                        });
                                                        editor.dispatch(transaction);

                                                        let cursorPos;
                                                        if (params && params.length > 0) {
                                                            cursorPos = insertPos + functionNameWithParentheses.length - 1;
                                                        } else {
                                                            cursorPos = insertPos + functionNameWithParentheses.length;
                                                        }
                                                        editor.dispatch({
                                                            selection: { anchor: cursorPos, head: cursorPos },
                                                        });
                                                        editor.focus();
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </Panel>
                            );
                        })
                }
            </Collapse>)
    }

    const fieldsRender = () => {
        const columns = [
            {
                title: 'Nome do Campo',
                dataIndex: 'field',
                key: 'field',
            },
            {
                title: 'Tipo do Campo',
                dataIndex: 'fieldtype',
                key: 'fieldtype',
            }
        ];
        return (<Table
            columns={columns}
            dataSource={formFields}
            size='small'
            scroll={{ y: 200, x: 200 }}
            pagination={false}
            onRow={(record, rowIndex) => {
                return {
                    onDoubleClick: event => {
                        const { field } = record;  // obtém o nome da função e os parâmetros
                        if (editorRef.current) {
                            const editor = editorRef.current.view;
                            const insertPos = editor.state.selection.main.head;
                            const functionNameWithParentheses = `[${field}]`;
                            const transaction = editor.state.update({
                                changes: { from: insertPos, insert: functionNameWithParentheses },
                            });
                            editor.dispatch(transaction);

                            let cursorPos = insertPos + functionNameWithParentheses.length;
                            editor.dispatch({
                                selection: { anchor: cursorPos, head: cursorPos },
                            });
                            editor.focus();
                        }
                    }
                }
            }}
        />)
    }

    const formsRender = () => {
        return (
            <Collapse defaultActiveKey={['1']}>
                {
                    _.map(
                        forms,
                        (value) => {
                            const columns = [
                                {
                                    title: 'Nome do Campo',
                                    dataIndex: 'field',
                                    key: 'field',
                                },
                                {
                                    title: 'Tipo do Campo',
                                    dataIndex: 'fieldtype',
                                    key: 'fieldtype',
                                    render: (text) => getFieldType(text)
                                }
                            ];
                            return (
                                <Panel header={
                                    <div>
                                        {
                                            value.name.replace("obj_", "")
                                        }
                                        <CopyOutlined onClick={(event) => {
                                            event.preventDefault()
                                            if (editorRef.current) {
                                                const editor = editorRef.current.view;
                                                const insertPos = editor.state.selection.main.head;
                                                const functionNameWithParentheses = `"${value.name.replace("obj_", "")}"`;
                                                const transaction = editor.state.update({
                                                    changes: {
                                                        from: insertPos,
                                                        insert: functionNameWithParentheses
                                                    },
                                                });
                                                editor.dispatch(transaction);

                                                let cursorPos = insertPos + functionNameWithParentheses.length;
                                                ;

                                                editor.dispatch({
                                                    selection: { anchor: cursorPos, head: cursorPos },
                                                });
                                                editor.focus();
                                            }
                                        }}></CopyOutlined>
                                    </div>
                                } key={value.name} ghost>
                                    <Table
                                        dataSource={value.fields}
                                        columns={columns}
                                        size='small' scroll={{ y: 200, x: 200 }}
                                        pagination={false}
                                        onRow={(record, rowIndex) => {
                                            return {
                                                onDoubleClick: event => {
                                                    const { field } = record;  // obtém o nome da função e os parâmetros
                                                    if (editorRef.current) {
                                                        const editor = editorRef.current.view;
                                                        const insertPos = editor.state.selection.main.head;
                                                        const functionNameWithParentheses = `"${field}"`;
                                                        const transaction = editor.state.update({
                                                            changes: {
                                                                from: insertPos,
                                                                insert: functionNameWithParentheses
                                                            },
                                                        });
                                                        editor.dispatch(transaction);

                                                        let cursorPos = insertPos + functionNameWithParentheses.length;
                                                        ;

                                                        editor.dispatch({
                                                            selection: { anchor: cursorPos, head: cursorPos },
                                                        });
                                                        editor.focus();
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </Panel>
                            );
                        })
                }
            </Collapse>
        )
        // return (<Table
        //     columns={columns}
        //     dataSource={forms}
        //     size='small'
        //     scroll={{y: 200, x: 200}}
        //     pagination={false}
        //     onRow={(record, rowIndex) => {
        //         return {
        //             onDoubleClick: event => {
        //                 const {name} = record;  // obtém o nome da função e os parâmetros
        //                 if (editorRef.current) {
        //                     const editor = editorRef.current.view;
        //                     const insertPos = editor.state.selection.main.head;
        //                     const functionNameWithParentheses = `{${name}}`;
        //                     const transaction = editor.state.update({
        //                         changes: {from: insertPos, insert: functionNameWithParentheses},
        //                     });
        //                     editor.dispatch(transaction);
        //
        //                     let cursorPos = insertPos + functionNameWithParentheses.length;
        //                     editor.dispatch({
        //                         selection: {anchor: cursorPos, head: cursorPos},
        //                     });
        //                     editor.focus();
        //                 }
        //             }
        //         }
        //     }}
        // />)
    }

    async function validateFormulaHandler() {
        setLoading(true)
        const data = await validateFormula(value, formName);
        setLoading(false)
        if (data.error) {
            let errors = [data.error];
            if (data.data) {
                errors = [...errors, data.data.join(", ")];
            }

            const fieldName = isValidationForm
                ? ['functionValidation', 'formula']
                : 'formula'

            form.setFields([{
                name: fieldName,
                errors,
            }]);
        }

    }

    return (
        <div style={{ display: 'flex', flexDirection: "column" }}>
            <div style={{ flex: 1, padding: "5px" }}>
                {!isValidationForm && (
                    <CodeMirror
                        ref={editorRef}
                        theme={isDarkMode ? materialDark : materialLight}
                        value={code}
                        height="100%"
                        extensions={[
                            javascript(),
                            basicSetup(),
                            autocompletion({
                                override: [completions],
                            }),
                        ]}
                        onChange={(value) => {
                            form.setFieldsValue({ formula: value })
                            onChange(value);
                            setValue(value)
                        }}
                    />
                )}
                {isValidationForm && (
                    <div style={{ marginTop: 15 }}>
                        {/* Codemirror para inserção da fórmula */}
                        <Form.Item
                            name={['functionValidation', 'formula']}
                            rules={[
                                {
                                    required: true,
                                    message: "Por favor, preencha a fórmula de validação."
                                },
                                {
                                    validator: (_, value) => {
                                        if (!value || value.trim() === "") {
                                            return Promise.reject("A fórmula não pode ser vazia.");
                                        }
                                        return Promise.resolve();
                                    }
                                },
                                {
                                    validateTrigger: 'onSubmit',
                                    validator: async (_, value) => {
                                        debugger;
                                        if (!value) return Promise.resolve();
                                        const result = await validateFormula(value, formName);
                                        if (result.error) {
                                            let errors = [result.error];
                                            if (result.data) {
                                                errors = [...errors, result.data.join(", ")];
                                            }
                                            return Promise.reject(errors);
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <CodeMirror
                                ref={editorRef}
                                theme={isDarkMode ? materialDark : materialLight}
                                value={code}
                                height="100%"
                                extensions={[
                                    javascript(),
                                    basicSetup(),
                                    autocompletion({
                                        override: [completions],
                                    }),
                                ]}
                                onChange={(value) => {
                                    form.setFieldsValue({ formula: value })
                                    onChange(value);
                                    setValue(value)
                                }}
                            />
                        </Form.Item>

                        {/* Radio para demonstrar ativado ou desativado para a validação */}
                        <p>Status da validação:</p>
                        <Form.Item
                            name={['functionValidation', 'active']} 
                            rules={[
                                {
                                    required: true,
                                    message: "Por favor, informe o status da validação."
                                }
                            ]}
                        >
                            <Radio.Group style={{ width: "100%" }}>
                                <Radio value={true}>Ativada</Radio>
                                <Radio value={false}>Desativada</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {/* Input para mensagem em caso de erro */}
                        <p>Retorno em caso de não validação:</p>
                        <Form.Item name={['functionValidation', 'errorMessage']}>
                            <Input />
                        </Form.Item>

                        <Button disabled={loading} loading={loading} type={"primary"}
                        onClick={validateFormulaHandler}>Validar fórmula</Button>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '6px' }}>
                    {!isValidationForm && (<Button
                        disabled={loading}
                        loading={loading}
                        style={{ marginTop: '5px' }}
                        type={"primary"}
                        onClick={() => previewFormulaHandler()}>
                        Pré-visualizar valores
                    </Button>)}

                    {(code && !isValidationForm && <Button disabled={loading} loading={loading} style={{ marginTop: '5px' }} type={"primary"}
                        onClick={validateFormulaHandler}>Validar fórmula</Button>)}
                </div>

                {showPreview && (
                    <>
                        <Button
                            style={{ marginTop: 10 }}
                            type="dashed"
                            onClick={() => setShowPreview(false)}
                        >
                            Fechar visualização
                        </Button>

                        <Table
                            style={{ marginTop: 10 }}
                            columns={previewColumns}
                            dataSource={previewData}
                            rowKey={(record, index) => index}
                            scroll={{ x: true }}
                            size="small"
                        />
                    </>
                )}
            </div>
            <div style={{ flex: 1, padding: "5px" }}>
                <Tabs
                    items={[
                        {
                            key: "item-1",
                            label: "Campos",
                            children: fieldsRender(),
                        },
                        {
                            key: "item-2",
                            label: "Formulários",
                            children: formsRender(),
                        },
                        {
                            key: "item-3",
                            label: "Funções",
                            children: functionsCollapseRender(),
                        },
                    ]}
                />
            </div>
        </div>
    );
}
